import { formatCoachingTime } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/format-coaching-time';
import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import {
  additionalTimeMinutes,
  scheduledDurationMinutes,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-duration';
import type {
  CoachingSession,
  CoachingSessionType,
  Cohort,
} from '@efdigital/ll-addon-data';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import gt from '@efdigital/ll-admin-v2/helpers/gt.ts';
import type { TOC } from '@ember/component/template-only';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';

const SESSION_TYPE_LABELS: Record<CoachingSessionType, string> = {
  chemistry: 'Chemistry',
  coaching: 'Coaching',
};

function sessionTypeLabel(type: CoachingSessionType): string {
  return SESSION_TYPE_LABELS[type];
}

function toDate(date: string): Date {
  return new Date(date);
}

<template>
  {{#let (getSessionStatusDisplay @coachingSession.status) as |statusDisplay|}}
    <div class="card border-{{statusDisplay.color}} rounded-4" ...attributes>
      <h3
        class="card-header h5 py-3 text-bg-{{statusDisplay.color}}
          border-{{statusDisplay.color}}
          rounded-top-4"
      >Overview</h3>
      <div class="card-body d-flex flex-column gap-3">
        <div class="row">
          <div
            class="col-12 col-lg-6 col-xl-12 d-flex flex-column gap-3 mb-3 mb-lg-0 mb-xl-3"
          >
            <div>
              <div class="form-label text-body-secondary mb-0">
                Session type
              </div>
              <div>
                {{sessionTypeLabel @coachingSession.sessionType}}
              </div>
            </div>

            {{#if @cohort}}
              <div>
                <div class="form-label text-body-secondary mb-0">
                  Cohort
                </div>
                <div class="text-break">
                  <LinkTo
                    @route="programs.program.cohort"
                    @models={{array @cohort.program.id @cohort.id}}
                  >
                    {{@cohort.title}}
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </div>
          <div class="col-12 col-lg-6 col-xl-12 d-flex flex-column gap-3">

            <div>
              <div class="form-label text-body-secondary mb-0">
                Status
              </div>
              <div>
                <span
                  class="badge rounded-pill bg-{{statusDisplay.color}}-subtle text-{{statusDisplay.color}}-emphasis fs-5 fw-semibold px-2 py-1 d-inline-flex align-items-center gap-1"
                  title={{statusDisplay.adminDescription}}
                >
                  <iconify-icon
                    inline
                    icon={{statusDisplay.icon}}
                    aria-hidden="true"
                  />
                  {{statusDisplay.uniqueLabel}}
                </span>
                <div class="form-text mt-1 lh-sm mb-0">
                  {{statusDisplay.adminDescription}}
                </div>
              </div>
            </div>
            <div>
              <div class="form-label text-body-secondary mb-0">
                Date and time
              </div>
              <div class="d-flex flex-row column-gap-3 row-gap-1 flex-wrap">
                <span>
                  {{formatDate (toDate @coachingSession.startTime)}}
                </span>
                <span>
                  {{formatTimeRange
                    (toDate @coachingSession.startTime)
                    (toDate @coachingSession.endTime)
                  }}
                </span>
              </div>
            </div>
            {{#if (eq @coachingSession.sessionType "coaching")}}
              {{#let
                (scheduledDurationMinutes @coachingSession)
                as |scheduledMinutes|
              }}
                <div>
                  <div class="form-label text-body-secondary mb-0">
                    Scheduled duration
                  </div>
                  <div>{{formatCoachingTime scheduledMinutes}}</div>
                </div>
                {{#if @coachingSession.confirmedDurationMinutes}}
                  <div>
                    <div class="form-label text-body-secondary mb-0">
                      Confirmed duration
                    </div>
                    <div>{{formatCoachingTime
                        @coachingSession.confirmedDurationMinutes
                      }}</div>
                  </div>
                  {{#let
                    (additionalTimeMinutes
                      scheduledMinutes @coachingSession.confirmedDurationMinutes
                    )
                    as |overrunMinutes|
                  }}
                    {{#if (gt overrunMinutes 0)}}
                      <div>
                        <div class="form-label text-body-secondary mb-0">
                          Additional time
                        </div>
                        <div>{{formatCoachingTime overrunMinutes}}</div>
                      </div>
                    {{/if}}
                  {{/let}}
                {{/if}}
              {{/let}}
            {{/if}}
          </div>
        </div>
      </div>
      {{yield to="actions"}}
    </div>
  {{/let}}
</template> satisfies TOC<{
  Args: {
    coachingSession: CoachingSession;
    cohort?: Cohort;
  };
  Blocks: {
    actions?: [];
  };
  Element: HTMLDivElement;
}>;
